/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/
import React, { useEffect, useState, useRef } from "react";
import {
  generateReport,
  getReportsPerSource,
  downloadReportExcel,
  downloadSumaryReportExcel,
  downloadReportPDF,
  getEventTypes,
  getAreas,
} from "../services/reportsService";
import ReportPreview from "../modules/ReportPreview";
import FilterController from "components/filter/controller/FilterController";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import { downloadFile } from "utils/fileUtils";
import { SUCCESS_MESSAGES, ERROR_MESSAGES } from "constants/messages";

/**
 * Controlador principal para la gestión de reportes
 * Maneja la lógica de negocio, estado y comunicación con servicios
 */
export function ReportsController() {
  // Estados principales
  const [reportData, setReportData] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataSourceId, setDataSourceId] = useState("");

  // Estados de paginación
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);

  // Estados para alertas
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  // Estados para filtros
  const [filters, setFilters] = useState({
    eventType: "",
    area: "",
    startDate: "",
    endDate: "",
    startTime: "",
    endTime: "",
    responsible: "",
    location: "",
  });

  // Configuración de filtros para el componente FilterController
  const dataFilter = useState([
    {
      field: "NOMBRE O APELLIDO",
      value: "name",
    },
    {
      field: "NÚMERO DE DOCUMENTO",
      value: "number_id",
    },
    {
      field: "CORREO ELECTRÓNICO",
      value: "email",
    },
    {
      field: "TIPO DE CARNET",
      value: "card_type",
    },
    {
      field: "ASISTENCIA",
      value: "attended",
    },
    {
      field: "PRIMERA ENTRADA",
      value: "first_entry",
    },
    {
      field: "ÚLTIMA SALIDA",
      value: "last_exit",
    },
    {
      field: "TOTAL DE LECTURAS MÍNIMO",
      value: "total_reads_min",
    },
    {
      field: "TOTAL DE LECTURAS MÁXIMO",
      value: "total_reads_max",
    },
  ]);
  const dataTable = "reports_attendance"; // Tabla indicada para filtros
  const filterControllerRef = useRef();

  /**
   * Efecto para cargar datos iniciales al montar el componente
   */
  useEffect(() => {
    //Obtener la url actual
    const currentUrl = window.location.href;
    // Extrae el ID de la fuente de datos de la URL actual dividiéndola por "/Fuentes/" y tomando la segunda parte del resultado
    const dataSourceId = currentUrl.split("/Fuentes/")[1];
    setDataSourceId(dataSourceId);
  }, [currentPage]);

  /**
   * Solicita los datos de asistencia para la fuente especificada
   * @param {string} sourceId - ID de la fuente de datos
   * @param {number} pageIndex - Índice de la página actual
   * @param {Array} appliedFilters - Filtros aplicados (opcional)
   */
  const requestAttendanceData = async (sourceId, pageIndex = 1, appliedFilters = []) => {
    if (!sourceId) return;

    try {
      // Construir parámetros de filtro para la API
      const filterParams = buildFilterParams(appliedFilters);

      const result = await getReportsPerSource(pageIndex, sourceId, filterParams);

      if (result.status !== false) {
        // Usar los datos reales de la API con paginación
        const { data, pagination } = result;

        setReportData(result);
        setTotalPages(pagination?.last_page || 1);
        setTotalData(pagination?.total || 0);
        setEntriesStart(pagination?.from || 0);
        setEntriesEnd(pagination?.to || 0);
        setLoading(false);
      } else {
        setError("Error al consultar los datos de asistencia");
        setLoading(false);
      }
    } catch (error) {
      setError("Error inesperado al cargar los datos de asistencia");
      setLoading(false);
    }
  };

  /**
   * Construye los parámetros de filtro para enviar a la API
   * @param {Array} appliedFilters - Array de filtros aplicados
   * @returns {Object} Objeto con parámetros de filtro para la API
   */
  const buildFilterParams = (appliedFilters) => {
    const params = {};

    appliedFilters.forEach((filter) => {
      switch (filter.type) {
        case "name":
          params.name = filter.value;
          break;
        case "number_id":
          params.number_id = filter.value;
          break;
        case "email":
          params.email = filter.value;
          break;
        case "card_type":
          params.card_type = filter.value;
          break;
        case "attended":
          // Convertir texto a booleano para el filtro de asistencia
          if (filter.value === "Asistieron" || filter.value === "true" || filter.value === "1") {
            params.attended = "true";
          } else if (
            filter.value === "No Asistieron" ||
            filter.value === "false" ||
            filter.value === "0"
          ) {
            params.attended = "false";
          }
          break;
        case "first_entry":
        case "last_exit":
          // Filtros de fecha con rango
          if (filter.value && filter.value.from && filter.value.until) {
            // Para fechas, el backend espera parámetros específicos
            // Nota: Estos filtros pueden requerir implementación adicional en el backend
            params[`${filter.type}_from`] = filter.value.from;
            params[`${filter.type}_until`] = filter.value.until;
          }
          break;
        case "total_reads_min":
          params.total_reads_min = filter.value;
          break;
        case "total_reads_max":
          params.total_reads_max = filter.value;
          break;
        default:
          break;
      }
    });

    return params;
  };

  /**
   * Función para llamar a handleClickSubmit desde el componente padre
   */
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  /**
   * Maneja el cambio de página en la paginación
   * @param {Event} event - Evento del cambio de página
   * @param {number} value - Número de la nueva página
   */
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  /**
   * Descarga el reporte en formato Excel
   */
  const handleDownloadExcel = async () => {
    if (!reportData || !dataSourceId) {
      handleAlert("fail", "No hay datos disponibles para descargar");
      return;
    }

    setLoading(true);

    try {
      const result = await downloadReportExcel(dataSourceId, filters);

      if (result.status !== false) {
        downloadFile(
          result.file,
          "Reporte de Asistencia",
          result.contentDisposition,
          (message) => handleAlert("success", message),
          (error) => handleAlert("fail", error),
          SUCCESS_MESSAGES.DOWNLOAD_COMPLETE_REPORT,
          ERROR_MESSAGES.DOWNLOAD_FAILED_REPORT
        );
      } else {
        handleAlert("fail", result.message || "Error al descargar el reporte");
      }
    } catch (error) {
      handleAlert("fail", "Error inesperado al descargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Descarga el reporte en formato PDF
   */
  const handleDownloadPDF = async () => {
    if (!reportData || !dataSourceId) {
      handleAlert("fail", "No hay datos disponibles para descargar");
      return;
    }

    setLoading(true);

    try {
      const result = await downloadReportPDF(dataSourceId, filters);

      if (result.status !== false) {
        downloadFile(
          result.file,
          "Reporte de Asistencia",
          result.contentDisposition,
          (message) => handleAlert("success", message),
          (error) => handleAlert("fail", error),
          SUCCESS_MESSAGES.DOWNLOAD_COMPLETE_REPORT,
          ERROR_MESSAGES.DOWNLOAD_FAILED_REPORT
        );
      } else {
        handleAlert("fail", result.message || "Error al descargar el reporte");
      }
    } catch (error) {
      handleAlert("fail", "Error inesperado al descargar el reporte");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Regresa a la vista anterior (placeholder para funcionalidad futura)
   */
  const handleBackToFilters = () => {
    // Funcionalidad para regresar a la vista anterior
    window.history.back();
  };

  /**
   * Maneja las alertas del sistema
   * @param {string} type - Tipo de alerta (success, fail, warning)
   * @param {string} msg - Mensaje a mostrar
   */
  const handleAlert = (type, msg) => {
    setTypeAlert(type);
    setMessage(msg);
    setIsAlert(true);
  };

  return (
    <>
      <Spinner loading={loading} />

      {dataSourceId && (
        <FilterController
          dataTable={dataTable}
          data={reportData}
          dataFilter={dataFilter}
          setListData={setReportData}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setTotalPages={setTotalPages}
          requestData={requestAttendanceData}
          setError={setError}
          setTotalData={setTotalData}
          setEntriesEnd={setEntriesEnd}
          setEntriesStart={setEntriesStart}
          setLoading={setLoading}
          dataSourceId={dataSourceId}
          ref={filterControllerRef}
        />
      )}

      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />

      <ReportPreview
        reportData={reportData}
        filters={filters}
        onDownloadExcel={handleDownloadExcel}
        onDownloadPDF={handleDownloadPDF}
        onBackToFilters={handleBackToFilters}
        loading={loading}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleAlert={handleAlert}
      />
    </>
  );
}
